import { getExperiment, getLayer } from '@eventbrite/statsig';
import { useEffect, useState } from 'react';

type EnrollmentVariant = string | null;

export enum StatsigEnrollmentType {
    LAYER = 'layer',
    EXPERIMENT = 'experiment',
}

export enum GetEnrollmentStatus {
    LOADING = 'loading',
    SUCCESS = 'success',
    DISABLED = 'disabled',
}

export type GetStatsigEnrollmentResponse =
    | {
          status: GetEnrollmentStatus.SUCCESS;
          variant: string;
      }
    | {
          status: GetEnrollmentStatus.DISABLED | GetEnrollmentStatus.LOADING;
          variant: null;
      };

const ENROLLMENT_FN_MAP = {
    [StatsigEnrollmentType.EXPERIMENT]: getExperiment,
    [StatsigEnrollmentType.LAYER]: getLayer,
};

interface IUseGetStatsigEnrollment {
    name: string;
    paramName: string;
    defaultValue: any;
    enabled?: boolean;
    enrollmentType?: StatsigEnrollmentType;
}

/**
 * **IMPORTANT**
 * Hook assumes the statsig SDK
 * has been initialized and returns the
 * expected variant. It is not listening
 * for the initialization to re-trigger
 * fetching an experiment.
 *
 * According to statsig docs, the `getExperiment` and `getLayer`
 * lookups are a synchronous operation and so should
 * have little latency overhead.
 *
 * Until mounted on the client, the hook will return
 * 'loading'. Allowing server side content to
 * render appropriately until the variant
 * is provided.
 *
 * @param name: Name of the layer or the experiment
 * @param paramName: Name of the param to retrieve the variant for
 * @returns GetStatsigEnrollmentResponse
 */
export function useGetStatsigEnrollment({
    name,
    paramName,
    defaultValue,
    enabled = true,
    enrollmentType = StatsigEnrollmentType.EXPERIMENT,
}: IUseGetStatsigEnrollment): GetStatsigEnrollmentResponse {
    const [enrollmentVariantValue, setEnrollmentVariantValue] =
        useState<EnrollmentVariant>(null);

    const [status, setGetEnrollmentStatus] = useState<GetEnrollmentStatus>(
        !enabled ? GetEnrollmentStatus.DISABLED : GetEnrollmentStatus.LOADING,
    );

    useEffect(() => {
        if (enabled) {
            const getVariant = ENROLLMENT_FN_MAP[enrollmentType];

            const variant = getVariant(name, paramName, defaultValue);

            setEnrollmentVariantValue(variant);
            setGetEnrollmentStatus(GetEnrollmentStatus.SUCCESS);
        }
    }, [name, paramName, defaultValue, enabled, enrollmentType]);

    return {
        status,
        variant: enrollmentVariantValue,
    } as GetStatsigEnrollmentResponse;
}
